import { useEffect, useMemo, useState } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  Plus,
  Trash2,
  Loader2,
  Hash,
  FileCode2,
  Lock,
  AlertTriangle,
  Power,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Separator,
} from '@/components/ui/primitives';
import {
  listThreatSignatures,
  createThreatSignature,
  updateThreatSignature,
  deleteThreatSignature,
  type ThreatSignaturesResponse,
  type CustomThreatSignature,
} from '@/lib/api';

type Kind = 'yara' | 'hash';

const SHA256_RE = /^[0-9a-fA-F]{64}$/;

const CATEGORY_VARIANT: Record<string, 'critical' | 'warning' | 'info' | 'glass'> = {
  'web-shell': 'critical',
  c2: 'critical',
  ransomware: 'critical',
  backdoor: 'critical',
  hacktool: 'warning',
  infostealer: 'warning',
  'macro-dropper': 'warning',
  loader: 'warning',
  heuristic: 'info',
  test: 'glass',
};

export function DiscoveryThreatSignatures() {
  const [data, setData] = useState<ThreatSignaturesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  /** id → pending mutation (toggle / delete), drives per-row spinner. */
  const [pending, setPending] = useState<Set<string>>(new Set());

  async function refresh() {
    try {
      const res = await listThreatSignatures();
      setData(res);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load threat signatures');
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, []);

  const markPending = (id: string, on: boolean) =>
    setPending((prev) => {
      const n = new Set(prev);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  async function toggleEnabled(sig: CustomThreatSignature) {
    markPending(sig.id, true);
    try {
      await updateThreatSignature(sig.id, { enabled: !sig.enabled });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Toggle failed');
    } finally {
      markPending(sig.id, false);
    }
  }

  async function remove(sig: CustomThreatSignature) {
    markPending(sig.id, true);
    try {
      await deleteThreatSignature(sig.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      markPending(sig.id, false);
    }
  }

  const builtin = data?.builtin ?? [];
  const custom = data?.custom ?? [];
  const enabledCount = useMemo(() => custom.filter((s) => s.enabled).length, [custom]);

  return (
    <PageContainer>
      <PageHeader
        title="Threat signatures"
        subtitle="The malware YARA / hash catalogue the file crawl runs in-pod. See the built-in signatures (metadata only) and add your own YARA rules + known-bad hash blocklists."
        badges={[
          { label: `${builtin.length} built-in`, variant: 'glass' },
          { label: `${custom.length} custom`, variant: 'info' },
          ...(enabledCount > 0
            ? [{ label: `${enabledCount} enabled`, variant: 'success' as const }]
            : []),
        ]}
        actions={
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" /> Add signature
          </Button>
        }
      />

      {/* Plain-language explainer - the buyers are not always technical, so define
          the two signature kinds in everyday terms before showing the catalogue. */}
      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <div className="glass-lite rounded-md p-3.5 border border-primary/20">
          <div className="flex items-center gap-2 mb-1.5">
            <FileCode2 className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-semibold text-foreground/90">What is a YARA rule?</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            A recipe that recognises malware by <em>how it is written or behaves</em> - the tell-tale
            code patterns antivirus looks for. Example: a Word file that hides a macro which secretly
            downloads a program is caught by the <code className="text-foreground/80">office_macro_dropper</code>{' '}
            rule, even if that exact file has never been seen before. Good at catching new variants of
            a known family.
          </p>
        </div>
        <div className="glass-lite rounded-md p-3.5 border border-sev-high/25">
          <div className="flex items-center gap-2 mb-1.5">
            <Hash className="h-4 w-4 text-sev-high shrink-0" />
            <span className="text-sm font-semibold text-foreground/90">What is a known-bad hash?</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            A unique fingerprint (a SHA-256 checksum) of a file <em>already confirmed</em> to be
            malicious. If a file on your systems has the same fingerprint, it is byte-for-byte the same
            bad file - an exact match, zero false positives. You paste in fingerprints from your own
            threat-intel feed (CERT-In, VirusTotal, your SOC) to block them everywhere.
          </p>
        </div>
      </div>

      {/* Privacy contract - internals stay in your pod, only metadata surfaces. */}
      <div className="glass-lite rounded-md p-3 mb-4 flex items-start gap-2 text-xs text-muted-foreground border border-primary/20">
        <Lock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <span>
          <strong className="text-foreground/90">Your data never leaves.</strong> Every signature runs
          inside the scanner pod in your own environment during the file crawl - the rule bodies and
          hash lists stay put. Only the match metadata (family, rule name, file path) surfaces as a
          finding. Use the built-in catalogue below, switch off anything you do not want, and add your
          own rules to extend it.
        </span>
      </div>

      {error && (
        <div className="glass-lite border border-sev-high/40 rounded-md p-3 mb-4 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
          <div className="flex-1 text-xs">
            <div className="font-medium">{error}</div>
          </div>
          <Button
            size="sm"
            variant="glass"
            onClick={() => {
              setError(null);
              setLoading(true);
              refresh().finally(() => setLoading(false));
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading threat signatures...
        </div>
      ) : (
        <div className="space-y-6">
          {/* Built-in catalogue - metadata only, read-only. */}
          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-4">
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldCheck className="h-4 w-4 text-primary" /> Built-in catalogue
                <Badge variant="glass" className="text-[9px]">
                  built-in, read-only
                </Badge>
              </CardTitle>
              <CardDescription>
                Curated malware signatures shipped with the platform. Metadata only - the rule bodies
                stay in the scanner pod.
              </CardDescription>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                    <th className="py-2 pr-3 font-medium">Signature</th>
                    <th className="py-2 pr-3 font-medium">Family</th>
                    <th className="py-2 pr-3 font-medium">Category</th>
                    <th className="py-2 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {builtin.map((s) => (
                    <tr key={s.name} className="border-b border-border/20 last:border-0 align-top">
                      <td className="py-2.5 pr-3">
                        <code className="font-mono text-xs text-foreground/95">{s.name}</code>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="text-xs">{s.family}</span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge variant={CATEGORY_VARIANT[s.category] ?? 'glass'} className="text-[9px]">
                          {s.category}
                        </Badge>
                      </td>
                      <td className="py-2.5 text-xs text-muted-foreground">{s.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>

          {/* Custom signatures - tenant-authored, add / enable-disable / delete. */}
          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <ShieldAlert className="h-4 w-4 text-sev-high" /> Your signatures
                  </CardTitle>
                  <CardDescription>
                    YARA rules and known-bad SHA-256 hashes you have added. Toggle to enable or
                    disable each one at scan time.
                  </CardDescription>
                </div>
                <Button variant="glass" size="sm" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            </CardHeader>

            {custom.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                <FileCode2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <div className="font-medium text-foreground/90">No custom signatures yet</div>
                <div className="mt-1">
                  Add a YARA rule or a known-bad hash to extend the built-in catalogue.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {custom.map((s) => {
                  const busy = pending.has(s.id);
                  return (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 rounded-md border border-border/30 px-3 py-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="shrink-0">
                        {s.kind === 'hash' ? (
                          <Hash className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <FileCode2 className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{s.name}</span>
                          <Badge variant="glass" className="text-[9px] uppercase">
                            {s.kind}
                          </Badge>
                          {s.family && (
                            <Badge variant="info" className="text-[9px]">
                              {s.family}
                            </Badge>
                          )}
                          <Badge variant={s.enabled ? 'success' : 'glass'} className="text-[9px]">
                            {s.enabled ? 'enabled' : 'disabled'}
                          </Badge>
                        </div>
                        <code className="font-mono text-[10px] text-muted-foreground break-all line-clamp-1">
                          {s.kind === 'hash' ? s.value : s.value.slice(0, 120)}
                        </code>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="glass"
                          size="sm"
                          disabled={busy}
                          onClick={() => void toggleEnabled(s)}
                          title={s.enabled ? 'Disable at scan time' : 'Enable at scan time'}
                        >
                          {busy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Power className="h-3.5 w-3.5" />
                          )}
                          {s.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          variant="glass"
                          size="sm"
                          disabled={busy}
                          onClick={() => void remove(s)}
                          title="Delete this signature"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </div>
      )}

      <AddSignatureDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => {
          setDialogOpen(false);
          void refresh();
        }}
      />
    </PageContainer>
  );
}

function AddSignatureDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  onCreated: () => void;
}) {
  const [kind, setKind] = useState<Kind>('yara');
  const [name, setName] = useState('');
  const [family, setFamily] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setKind('yara');
      setName('');
      setFamily('');
      setValue('');
      setErr(null);
      setSaving(false);
    }
  }, [open]);

  const hashInvalid = kind === 'hash' && value.trim().length > 0 && !SHA256_RE.test(value.trim());
  const canSave = name.trim().length > 0 && value.trim().length > 0 && !hashInvalid && !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setErr(null);
    try {
      await createThreatSignature({
        kind,
        name: name.trim(),
        family: family.trim() || undefined,
        value: value.trim(),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add threat signature
          </DialogTitle>
          <DialogDescription>
            Extend the built-in catalogue with your own YARA rule or known-bad hash.
          </DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
              Kind
            </label>
            <div className="flex gap-2">
              {(['yara', 'hash'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={
                    'flex-1 rounded-md border px-3 py-2 text-sm transition-colors ' +
                    (kind === k
                      ? 'border-primary/50 bg-primary/10 text-foreground'
                      : 'border-border/40 text-muted-foreground hover:bg-muted/40')
                  }
                >
                  {k === 'yara' ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <FileCode2 className="h-3.5 w-3.5" /> YARA rule
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      <Hash className="h-3.5 w-3.5" /> Known-bad hash
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. acme_custom_webshell"
                maxLength={120}
                className="w-full glass-lite rounded px-2 py-1.5 text-sm bg-transparent border border-border/40 focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                Family (optional)
              </label>
              <input
                value={family}
                onChange={(e) => setFamily(e.target.value)}
                placeholder="e.g. PHP.WebShell"
                maxLength={80}
                className="w-full glass-lite rounded px-2 py-1.5 text-sm bg-transparent border border-border/40 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
              {kind === 'yara' ? 'YARA rule' : 'SHA-256 hash (64 hex)'}
            </label>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={kind === 'yara' ? 7 : 2}
              placeholder={
                kind === 'yara'
                  ? 'rule acme_custom_webshell {\n  strings:\n    $a = "eval($_POST"\n  condition:\n    $a\n}'
                  : 'e.g. 44d88612fea8a8f36de82e1278abb02f...'
              }
              className="w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary resize-y"
            />
            {hashInvalid && (
              <p className="text-[11px] text-sev-high mt-1">
                A hash signature must be a 64-character hex SHA-256 digest.
              </p>
            )}
          </div>

          <div className="glass-lite rounded-md p-2.5 text-[11px] text-muted-foreground flex items-start gap-2 border border-primary/20">
            <Lock className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span>
              This runs in-pod during the file crawl. The rule body / hash stays in your scanner
              environment - only match metadata (family, rule name, file path) leaves as a finding.
            </span>
          </div>

          {err && (
            <div className="text-xs text-sev-high glass-lite border border-sev-high/40 rounded-md p-2">
              {err}
            </div>
          )}
        </div>

        <Separator />

        <div className="flex items-center justify-end gap-2">
          <Button variant="glass" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={!canSave}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add signature
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
